import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useTheme,
  Chip,
  useMediaQuery,
  SelectChangeEvent,
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { WorkoutUtils } from '../../utils/workoutUtils';
import { UserProgress } from '../../types/workout';

interface WeeklyProgressChartsProps {
  userProgress: UserProgress | null;
}

type ChartType = 'completion-trend' | 'completed-vs-missed' | 'consistency-heatmap';

export const WeeklyProgressCharts: React.FC<WeeklyProgressChartsProps> = ({ userProgress }) => {
  const [selectedChart, setSelectedChart] = useState<ChartType>('completion-trend');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleChartChange = (event: SelectChangeEvent) => {
    setSelectedChart(event.target.value as ChartType);
  };

  const chartOptions = [
    { value: 'completion-trend', label: 'Weekly Completion Trend', description: 'Track completion percentage over 12 weeks' },
    { value: 'completed-vs-missed', label: 'Completed vs Missed Workouts', description: 'Compare completed and missed workouts by week' },
    { value: 'consistency-heatmap', label: 'Workout Consistency Calendar', description: 'Visual calendar of workout completion' },
  ];

  const selectedOption = chartOptions.find(option => option.value === selectedChart);

  const renderChart = () => {
    if (!userProgress || userProgress.workoutHistory.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No workout data available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Complete some workouts to see your progress charts
          </Typography>
        </Box>
      );
    }

    switch (selectedChart) {
      case 'completion-trend':
        const weeklyData = WorkoutUtils.getWeeklyCompletionData(userProgress);
        
        return (
          <Box sx={{ 
            width: '100%', 
            height: isMobile ? 300 : 400,
            '& .recharts-wrapper': {
              pointerEvents: 'none !important'
            }
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={weeklyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="week" 
                  label={{ value: 'Week', position: 'insideBottom', offset: -5 }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis 
                  label={{ value: 'Completion %', angle: -90, position: 'insideLeft' }}
                  domain={[0, 100]}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <Tooltip 
                  formatter={(value: number) => [`${value}%`, 'Completion Rate']}
                  labelFormatter={(week) => `Week ${week}`}
                />
                <Line 
                  type="monotone" 
                  dataKey="percentage" 
                  stroke="#8884d8" 
                  strokeWidth={3}
                  dot={{ fill: '#8884d8', strokeWidth: 2, r: isMobile ? 4 : 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        );

      case 'completed-vs-missed':
        const weeklyCompletionData = WorkoutUtils.getWeeklyCompletionData(userProgress);
        const currentWeek = userProgress.currentWeek;
        
        // Enhanced data with week status and skipped tracking
        const enhancedData = weeklyCompletionData.map(week => {
          const isFutureWeek = week.week > currentWeek;
          const isCurrentWeek = week.week === currentWeek;
          const completed = week.completedDays.length;
          const skipped = week.skippedDays.length;
          const missed = week.total - completed - skipped;
          
          return {
            ...week,
            completed,
            skipped,
            missed,
            isFutureWeek,
            isCurrentWeek
          };
        });

        const getBarColor = (week: any, dataKey: string) => {
          if (week.isFutureWeek) {
            return '#e0e0e0'; // Gray for future weeks
          }
          if (dataKey === 'completed') {
            return '#4caf50'; // Green for completed
          }
          if (dataKey === 'skipped') {
            return '#ff9800'; // Orange for skipped
          }
          if (week.isCurrentWeek && dataKey === 'missed') {
            return '#ffa726'; // Light orange for current week in progress
          }
          return '#f44336'; // Red for actually missed (past weeks)
        };

        return (
          <Box sx={{ 
            width: '100%', 
            height: isMobile ? 300 : 400,
            '& .recharts-wrapper': {
              pointerEvents: 'none !important'
            }
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={enhancedData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="week" 
                  label={{ value: 'Week', position: 'insideBottom', offset: -5 }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis 
                  label={{ value: 'Workouts', angle: -90, position: 'insideLeft' }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <Tooltip 
                  formatter={(value: number, name: string, props: any) => {
                    const week = props.payload;
                    if (name === 'completed') return [value, 'Completed'];
                    if (name === 'skipped') return [value, 'Skipped'];
                    if (week.isFutureWeek) return [value, 'Not Reached Yet'];
                    if (week.isCurrentWeek) return [value, 'In Progress'];
                    return [value, 'Missed'];
                  }}
                  labelFormatter={(week) => `Week ${week}`}
                />
                <Bar dataKey="completed" stackId="workouts" name="completed">
                  {enhancedData.map((entry, index) => (
                    <Cell key={`completed-${index}`} fill={getBarColor(entry, 'completed')} />
                  ))}
                </Bar>
                <Bar dataKey="skipped" stackId="workouts" name="skipped">
                  {enhancedData.map((entry, index) => (
                    <Cell key={`skipped-${index}`} fill={getBarColor(entry, 'skipped')} />
                  ))}
                </Bar>
                <Bar dataKey="missed" stackId="workouts" name="missed">
                  {enhancedData.map((entry, index) => (
                    <Cell key={`missed-${index}`} fill={getBarColor(entry, 'missed')} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        );

      case 'consistency-heatmap':
        const heatmapData = WorkoutUtils.getWorkoutHeatmapData(userProgress);
        
        // Group by week for heatmap display
        const weeklyHeatmap: { [week: string]: any[] } = heatmapData.reduce((acc, day) => {
          if (!acc[day.week]) acc[day.week] = [];
          acc[day.week].push(day);
          return acc;
        }, {} as { [week: string]: any[] });

        return (
          <Box sx={{ 
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#888',
              borderRadius: 4,
            },
          }}>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(12, 1fr)', 
              gap: 1,
              minWidth: isMobile ? 600 : 'auto',
              p: 2
            }}>
              {Object.entries(weeklyHeatmap).map(([week, days]) => (
                <Box key={week} sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ mb: 1, display: 'block' }}>
                    W{week}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {days.map((day) => {
                      let backgroundColor = '#f5f5f5'; // Rest day default
                      let status = 'Rest Day';
                      
                      if (day.workoutDay) {
                        if (day.completed) {
                          backgroundColor = '#4caf50'; // Green for completed
                          status = 'Completed';
                        } else if (day.skipped) {
                          backgroundColor = '#ff9800'; // Orange for skipped
                          status = 'Skipped';
                        } else {
                          backgroundColor = '#ffcdd2'; // Light red for missed
                          status = 'Missed';
                        }
                      }
                      
                      return (
                        <Box
                          key={`${week}-${day.day}`}
                          sx={{
                            width: isMobile ? 20 : 24,
                            height: isMobile ? 20 : 24,
                            borderRadius: 1,
                            backgroundColor,
                            border: '1px solid #e0e0e0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          title={`${day.day} - Week ${week} - ${status}`}
                        >
                          <Typography variant="caption" sx={{ fontSize: isMobile ? '0.6rem' : '0.7rem' }}>
                            {day.day.charAt(0)}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </Box>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, backgroundColor: '#4caf50', borderRadius: 1 }} />
                <Typography variant="caption">Completed</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, backgroundColor: '#ff9800', borderRadius: 1 }} />
                <Typography variant="caption">Skipped</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, backgroundColor: '#ffcdd2', borderRadius: 1 }} />
                <Typography variant="caption">Missed</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, backgroundColor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }} />
                <Typography variant="caption">Rest Day</Typography>
              </Box>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Card>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'flex-start' }, 
          mb: 3,
          gap: { xs: 2, sm: 0 }
        }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
              Weekly Progress Analysis
            </Typography>
            {selectedOption && (
              <Typography variant="body2" color="text.secondary">
                {selectedOption.description}
              </Typography>
            )}
          </Box>
          
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 250 } }}>
            <Select
              value={selectedChart}
              onChange={handleChartChange}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                    zIndex: 1400,
                  },
                },
              }}
            >
              {chartOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {option.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {renderChart()}
      </CardContent>
    </Card>
  );
}; 